import { isMobile } from "react-device-detect";
import { useLocation, useNavigate } from "react-router-dom";

import {
  EnrollmentSuccessType,
  ErrorState,
  RetriageFinishedStatus,
  ScreenedOutReason,
} from "@/constants";
import { useAppContext } from "@/contexts";
import { EligibilityCandidateMatch } from "@/models";
import { getAutologinData } from "@/utils/cookies";
import { buildDeeplink } from "@/utils/deeplink";

export const ELIGIBILITY_DISAMBIGUATION_ROUTE = "/eligibility-disambiguation";

export const useGoto = () => {
  const {
    setCandidateMatches,
    setScreenedOutReason,
    setErrorState,
    setRetriageFinishedStatus,
  } = useAppContext();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const success = (type: EnrollmentSuccessType) => {
    const qs = new URLSearchParams();
    qs.append("type", type);
    navigate({
      pathname: "/success",
      search: qs.toString(),
    });
  };

  const error = (errorState?: ErrorState) => {
    if (errorState) {
      setErrorState(errorState);
    }
    navigate({
      pathname: "/error",
      search,
    });
  };

  const eligibilityDisambiguation = (matches: EligibilityCandidateMatch[]) => {
    setCandidateMatches(matches);
    navigate({
      pathname: ELIGIBILITY_DISAMBIGUATION_ROUTE,
      search,
    });
  };

  const notFound = () => {
    navigate({
      pathname: "/not-found",
    });
  };

  const retriageFinished = (status: RetriageFinishedStatus) => {
    setRetriageFinishedStatus(status);
    navigate({
      pathname: "/rt/finished",
    });
  };

  const medIntake = (userId: string) => {
    window.location.href = `${window.ENV.BUYFLOW_CLIENT_URL}/telehealth?userId=${userId}&isB2B=true#/steadyPatientIntake`;
  };

  /** Starts the regular B2C (Healthy Weight) onboarding. No data is passed. */
  const b2cOnboarding = () => {
    window.location.href = window.ENV.BUYFLOW_CLIENT_URL;
  };

  const screenOut = (reason: ScreenedOutReason) => {
    setScreenedOutReason(reason);

    // This logic redirects to the screen out page by taking the current
    // pathname and replacing the last segment with the /ineligible route.
    // This is needed to support flows with different segment lengths.
    const segments = pathname.split("/");
    const baseRoute = segments.slice(0, segments.length - 1).join("/");
    navigate({ pathname: `${baseRoute}/ineligible`, search });
  };

  const openApp = (email?: string) => {
    const autologinData = getAutologinData();
    if (autologinData) {
      window.location.href = "https://unknown.noom.com/?score=1&close=1";
    } else if (isMobile) {
      window.location.href = buildDeeplink(email);
    }
  };

  return {
    b2cOnboarding,
    eligibilityDisambiguation,
    error,
    medIntake,
    notFound,
    openApp,
    retriageFinished,
    screenOut,
    success,
  };
};
